import React, { useCallback, useEffect, useMemo, useState } from "react";
import simpleFarmService from "../../../shared/api/farming/simpleFarmService";
import { Button, Field, Notice, PageTitle, Stat, TableShell, card, input, td, th, todayIso } from "./farmUi";

const UNITS = ["kg", "g", "liter", "ml", "packet", "bag", "unit"];
const blank = () => ({ ingredientName: "", quantityInward: "", unit: "kg", minimumStockAlert: "", inwardDate: todayIso(), remarks: "" });
const restockBlank = () => ({ stockId: null, quantity: "", inwardDate: todayIso(), remarks: "" });

const badge = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 999,
  padding: "5px 9px",
  fontWeight: 900,
  fontSize: 12,
  color: tone === "bad" ? "#fecaca" : tone === "info" ? "#bfdbfe" : "#bbf7d0",
  background: tone === "bad" ? "rgba(239,68,68,0.16)" : tone === "info" ? "rgba(59,130,246,0.14)" : "rgba(34,197,94,0.14)",
  border: tone === "bad" ? "1px solid rgba(239,68,68,0.35)" : tone === "info" ? "1px solid rgba(59,130,246,0.30)" : "1px solid rgba(34,197,94,0.30)",
});

const tabStyle = (active) => ({
  border: active ? "1px solid rgba(99,102,241,0.60)" : "1px solid rgba(148,163,184,0.18)",
  background: active ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : "rgba(15,23,42,0.96)",
  color: active ? "#fff" : "#cbd5e1",
  padding: "11px 16px",
  borderRadius: 14,
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: active ? "0 12px 28px rgba(99,102,241,0.28)" : "none",
});

function asNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function formatQty(value) {
  const n = asNumber(value);
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
}

function isLowRow(row) {
  return Boolean(row.lowStock) || (asNumber(row.minimumStockAlert) > 0 && asNumber(row.availableQuantity) <= asNumber(row.minimumStockAlert));
}

export default function StockPage() {
  const [activeTab, setActiveTab] = useState("stock");
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyStockId, setHistoryStockId] = useState("");
  const [form, setForm] = useState(blank());
  const [restockForm, setRestockForm] = useState(restockBlank());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [savingRestock, setSavingRestock] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const lowRows = useMemo(() => rows.filter(isLowRow), [rows]);
  const selectedRestockStock = useMemo(() => rows.find((r) => String(r.id) === String(restockForm.stockId)) || null, [rows, restockForm.stockId]);

  const filteredHistoryRows = useMemo(() => {
    if (!historyStockId) return historyRows;
    return historyRows.filter((r) => String(r.stockId) === String(historyStockId));
  }, [historyRows, historyStockId]);

  const historySummary = useMemo(() => {
    const restockRows = filteredHistoryRows.filter((r) => String(r.actionType || "").toUpperCase() === "RESTOCK");
    const totalAdded = restockRows.reduce((sum, r) => sum + asNumber(r.quantityAdded), 0);
    return { count: restockRows.length, totalAdded };
  }, [filteredHistoryRows]);

  const stats = useMemo(() => ({
    total: rows.length,
    low: lowRows.length,
  }), [rows.length, lowRows.length]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistoryRows(await simpleFarmService.stocks.restockHistory());
    } catch (e) {
      setError(simpleFarmService._apiMsg(e));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await simpleFarmService.stocks.list());
    } catch (e) {
      setError(simpleFarmService._apiMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setRestock = (k, v) => setRestockForm((p) => ({ ...p, [k]: v }));
  const reset = () => { setForm(blank()); setEditingId(null); };
  const closeRestock = () => setRestockForm(restockBlank());

  function openRestock(row) {
    setError("");
    setMessage("");
    setActiveTab("stock");
    setRestockForm({ stockId: row.id, quantity: "", inwardDate: todayIso(), remarks: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setMessage("");
    const payload = {
      ingredientName: form.ingredientName.trim(),
      quantityInward: Number(form.quantityInward),
      unit: form.unit,
      minimumStockAlert: form.minimumStockAlert === "" ? 0 : Number(form.minimumStockAlert),
      inwardDate: form.inwardDate,
      remarks: form.remarks,
    };
    try {
      if (editingId) await simpleFarmService.stocks.update(editingId, payload);
      else await simpleFarmService.stocks.create(payload);
      setMessage(editingId ? "Stock updated successfully." : "Stock added successfully.");
      reset();
      await load();
      await loadHistory();
    } catch (e2) {
      setError(simpleFarmService._apiMsg(e2));
    }
  }

  async function submitRestock(e) {
    e.preventDefault();
    if (!restockForm.stockId) return;
    setSavingRestock(true);
    setError("");
    setMessage("");
    try {
      await simpleFarmService.stocks.restock(restockForm.stockId, {
        quantity: Number(restockForm.quantity),
        inwardDate: restockForm.inwardDate,
        remarks: restockForm.remarks,
      });
      setMessage(`${selectedRestockStock?.ingredientName || "Stock"} restocked successfully. History updated.`);
      closeRestock();
      await load();
      await loadHistory();
    } catch (e2) {
      setError(simpleFarmService._apiMsg(e2));
    } finally {
      setSavingRestock(false);
    }
  }

  function edit(r) {
    setEditingId(r.id);
    setForm({ ingredientName: r.ingredientName || "", quantityInward: r.quantityInward ?? "", unit: r.unit || "kg", minimumStockAlert: r.minimumStockAlert ?? "", inwardDate: r.inwardDate || todayIso(), remarks: r.remarks || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function remove(r) {
    if (!window.confirm(`Delete ${r.ingredientName}?`)) return;
    try {
      await simpleFarmService.stocks.remove(r.id);
      setMessage("Stock deleted.");
      await load();
      await loadHistory();
    } catch (e) {
      setError(simpleFarmService._apiMsg(e));
    }
  }

  function showHistoryFor(row) {
    setHistoryStockId(String(row.id));
    setActiveTab("history");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div style={{ color: "#f8fafc" }}>
      <PageTitle title="Ingredient Stock" subtitle="Add raw ingredients here. Low stock items can be restocked into the same item, and every restock is saved in history with date, quantity, before/after stock, and remarks.">
        <div style={{ display: "flex", gap: 10 }}>
          <Stat value={stats.total} labelText="Items" />
          <Stat value={stats.low} labelText="Low stock" tone={stats.low ? "bad" : "good"} />
          <Stat value={historySummary.count} labelText="Restocks" tone="good" />
        </div>
      </PageTitle>
      {error && <Notice type="error">{error}</Notice>}
      {message && <Notice type="success">{message}</Notice>}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
        <button type="button" style={tabStyle(activeTab === "stock")} onClick={() => setActiveTab("stock")}>Stock & Restock</button>
        <button type="button" style={tabStyle(activeTab === "history")} onClick={() => { setActiveTab("history"); loadHistory(); }}>Restock History</button>
      </div>

      {activeTab === "history" && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
            <div>
              <h2 style={{ margin: "0 0 6px" }}>Restock History</h2>
              <p style={{ margin: 0, color: "#94a3b8" }}>Shows every initial stock entry and every restock with quantity, unit, before/after stock, date, and remarks.</p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
              <Field labelText="Filter Ingredient">
                <select style={{ ...input, minWidth: 220 }} value={historyStockId} onChange={(e) => setHistoryStockId(e.target.value)}>
                  <option value="">All ingredients</option>
                  {rows.map((r) => <option key={r.id} value={r.id}>{r.ingredientName}</option>)}
                </select>
              </Field>
              <Button variant="muted" onClick={loadHistory}>{historyLoading ? "Loading..." : "Refresh History"}</Button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginBottom: 16 }}>
            <Stat value={filteredHistoryRows.length} labelText="History rows" />
            <Stat value={historySummary.count} labelText="Restock entries" tone="good" />
            <Stat value={formatQty(historySummary.totalAdded)} labelText="Total restocked qty" tone="good" />
          </div>

          <TableShell>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Ingredient</th>
                <th style={th}>Type</th>
                <th style={th}>Added Qty</th>
                <th style={th}>Available Before</th>
                <th style={th}>Available After</th>
                <th style={th}>Total Inward Before</th>
                <th style={th}>Total Inward After</th>
                <th style={th}>Remarks</th>
                <th style={th}>Saved At</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistoryRows.map((h) => {
                const isRestock = String(h.actionType || "").toUpperCase() === "RESTOCK";
                return (
                  <tr key={h.id}>
                    <td style={td}>{h.restockDate || "-"}</td>
                    <td style={td}><b>{h.ingredientName}</b><div style={{ color: "#64748b" }}>Stock ID: {h.stockId}</div></td>
                    <td style={td}><span style={badge(isRestock ? "good" : "info")}>{isRestock ? "Restock" : "Initial Stock"}</span></td>
                    <td style={{ ...td, color: "#86efac", fontWeight: 900 }}>{formatQty(h.quantityAdded)} {h.unit}</td>
                    <td style={td}>{formatQty(h.availableBefore)} {h.unit}</td>
                    <td style={td}>{formatQty(h.availableAfter)} {h.unit}</td>
                    <td style={td}>{formatQty(h.inwardBefore)} {h.unit}</td>
                    <td style={td}>{formatQty(h.inwardAfter)} {h.unit}</td>
                    <td style={{ ...td, color: "#cbd5e1", minWidth: 180 }}>{h.remarks || "-"}</td>
                    <td style={td}>{formatDateTime(h.createdAt)}</td>
                  </tr>
                );
              })}
              {!filteredHistoryRows.length && <tr><td style={td} colSpan="10">No restock history yet. Add or restock an ingredient to create history.</td></tr>}
            </tbody>
          </TableShell>
        </div>
      )}

      {activeTab === "stock" && (
        <>
          {selectedRestockStock && (
            <form onSubmit={submitRestock} style={{ ...card, marginBottom: 20, borderColor: isLowRow(selectedRestockStock) ? "rgba(251,113,133,0.46)" : "rgba(34,197,94,0.35)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                <div>
                  <h2 style={{ margin: "0 0 6px" }}>Restock: {selectedRestockStock.ingredientName}</h2>
                  <p style={{ margin: 0, color: "#94a3b8" }}>
                    Current available: <b style={{ color: "#f8fafc" }}>{formatQty(selectedRestockStock.availableQuantity)} {selectedRestockStock.unit}</b> ·
                    Minimum alert: <b style={{ color: "#f8fafc" }}>{formatQty(selectedRestockStock.minimumStockAlert)} {selectedRestockStock.unit}</b>
                  </p>
                </div>
                <Button variant="muted" onClick={closeRestock}>Close</Button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
                <Field labelText={`Add Quantity (${selectedRestockStock.unit || "unit"}) *`}>
                  <input style={input} required type="number" step="0.001" min="0.001" value={restockForm.quantity} onChange={(e) => setRestock("quantity", e.target.value)} placeholder="e.g., 25" />
                </Field>
                <Field labelText="Restock Date">
                  <input style={input} type="date" value={restockForm.inwardDate} onChange={(e) => setRestock("inwardDate", e.target.value)} />
                </Field>
                <Field labelText="Restock Remarks">
                  <input style={input} value={restockForm.remarks} onChange={(e) => setRestock("remarks", e.target.value)} placeholder="Supplier / bill / reason" />
                </Field>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <Button type="submit" variant="blue" disabled={savingRestock}>{savingRestock ? "Saving..." : "Save Restock"}</Button>
                <Button variant="muted" onClick={closeRestock}>Cancel</Button>
                <Button variant="purple" onClick={() => showHistoryFor(selectedRestockStock)}>View History</Button>
              </div>
            </form>
          )}

          {lowRows.length > 0 && (
            <div style={{ ...card, marginBottom: 20, borderColor: "rgba(251,113,133,0.35)", background: "linear-gradient(135deg,rgba(127,29,29,0.22),rgba(15,23,42,0.94))" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
                <div>
                  <h2 style={{ margin: "0 0 6px" }}>Low Stock Restock Center</h2>
                  <p style={{ margin: 0, color: "#fca5a5" }}>These items reached or crossed their minimum stock alert. Click Restock to add quantity to the same item.</p>
                </div>
                <Button variant="muted" onClick={load}>{loading ? "Loading..." : "Refresh"}</Button>
              </div>
              <TableShell>
                <thead>
                  <tr><th style={th}>Ingredient</th><th style={th}>Available</th><th style={th}>Min Alert</th><th style={th}>Need To Reach Min</th><th style={th}>Action</th></tr>
                </thead>
                <tbody>
                  {lowRows.map((r) => {
                    const need = Math.max(0, asNumber(r.minimumStockAlert) - asNumber(r.availableQuantity));
                    return <tr key={`low-${r.id}`}>
                      <td style={td}><b>{r.ingredientName}</b><div style={{ color: "#94a3b8" }}>{r.remarks}</div></td>
                      <td style={{ ...td, color: "#fb7185", fontWeight: 900 }}>{formatQty(r.availableQuantity)} {r.unit}</td>
                      <td style={td}>{formatQty(r.minimumStockAlert)} {r.unit}</td>
                      <td style={td}>{formatQty(need)} {r.unit}</td>
                      <td style={td}><Button variant="blue" onClick={() => openRestock(r)}>Restock</Button></td>
                    </tr>;
                  })}
                </tbody>
              </TableShell>
            </div>
          )}

          <form onSubmit={submit} style={{ ...card, marginBottom: 20 }}>
            <h2 style={{ margin: "0 0 14px" }}>{editingId ? "Update Stock" : "Add New Ingredient"}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
              <Field labelText="Ingredient Name *"><input style={input} required value={form.ingredientName} onChange={(e) => set("ingredientName", e.target.value)} /></Field>
              <Field labelText="Quantity Inward *"><input style={input} required type="number" step="0.001" min="0.001" value={form.quantityInward} onChange={(e) => set("quantityInward", e.target.value)} /></Field>
              <Field labelText="Unit *"><select style={input} value={form.unit} onChange={(e) => set("unit", e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></Field>
              <Field labelText="Minimum Stock Alert"><input style={input} type="number" step="0.001" min="0" value={form.minimumStockAlert} onChange={(e) => set("minimumStockAlert", e.target.value)} /></Field>
              <Field labelText="Date"><input style={input} type="date" value={form.inwardDate} onChange={(e) => set("inwardDate", e.target.value)} /></Field>
              <Field labelText="Remarks"><input style={input} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></Field>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <Button type="submit">{editingId ? "Update Stock" : "Add Stock"}</Button>
              {editingId && <Button variant="muted" onClick={reset}>Cancel</Button>}
            </div>
          </form>

          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Stock List</h2>
              <Button variant="muted" onClick={load}>{loading ? "Loading..." : "Refresh"}</Button>
            </div>
            <TableShell><thead><tr><th style={th}>Ingredient</th><th style={th}>Inward</th><th style={th}>Available</th><th style={th}>Unit</th><th style={th}>Min Alert</th><th style={th}>Date</th><th style={th}>Status</th><th style={th}>Action</th></tr></thead><tbody>
              {rows.map((r) => {
                const low = isLowRow(r);
                return <tr key={r.id} style={{ background: low ? "rgba(127,29,29,0.12)" : "transparent" }}>
                  <td style={td}><b>{r.ingredientName}</b><div style={{ color: "#64748b", whiteSpace: "pre-line" }}>{r.remarks}</div></td>
                  <td style={td}>{formatQty(r.quantityInward)}</td>
                  <td style={{ ...td, color: low ? "#fb7185" : "#86efac", fontWeight: 900 }}>{formatQty(r.availableQuantity)}</td>
                  <td style={td}>{r.unit}</td>
                  <td style={td}>{formatQty(r.minimumStockAlert)}</td>
                  <td style={td}>{r.inwardDate || "-"}</td>
                  <td style={td}><span style={badge(low ? "bad" : "good")}>{low ? "Low Stock" : "OK"}</span></td>
                  <td style={td}><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button variant="blue" onClick={() => openRestock(r)}>Restock</Button><Button variant="purple" onClick={() => showHistoryFor(r)}>History</Button><Button variant="muted" onClick={() => edit(r)}>Edit</Button><Button variant="danger" onClick={() => remove(r)}>Delete</Button></div></td>
                </tr>;
              })}
              {!rows.length && <tr><td style={td} colSpan="8">No stock added yet.</td></tr>}
            </tbody></TableShell>
          </div>
        </>
      )}
    </div>
  );
}
